/* ============================================================================
   Stickr Lab — Builder Core v1  (assets/sl-builder-core.js)

   The Builder's ORCHESTRATION layer. The React Builder calls this to drive the
   three approved engines and to hold its centralized functional state. It
   ORCHESTRATES; it does not replace or duplicate any engine:
     - SLUpload  → file detection, parsing, metadata, DPI, warnings
     - SLPricing → die-cut pricing
     - SLSheets  → sheet layout, yield, trimming, eligibility

   There is NO pricing formula, NO layout math, and NO file parsing in this file —
   only calls into the engines plus derivation of the combined state. Framework-
   independent (no React/DOM/Shopify), so the whole integration is Node-testable.

   Product rules (finished-size min/max/increments) are supplied to SLUpload via its
   `rules` hook — they are NOT placed back inside SLUpload (which stays product-agnostic).

   Out of scope (not here): cart, checkout, storage, accounts, proofs, background
   removal, Sticker Sheet prices (unavailable), material surcharges (none).
   ============================================================================ */
(function (root) {
  'use strict';

  function defaultEngines() {
    var g = (typeof window !== 'undefined') ? window : {};
    return { SLUpload: g.SLUpload, SLPricing: g.SLPricing, SLSheets: g.SLSheets, SLCartGraphics: g.SLCartGraphics };
  }

  // Approved Builder finished-dimension rules: min 0.5″, max 10.5″, 0.25″ increments.
  // Returned as SLUpload `rules` hooks (each receives file metadata; here they validate
  // the customer-chosen finished size closed over below). Never mutate SLUpload with these.
  /* INDIVIDUAL STICKERS ARE AT LEAST 1 INCH (Ryan, 24 Oct 2026). `sizeRule`:
       'diecut'  a die cut (proportions locked to the art): 1″ on its LONGEST side;
       'shape'   a circle/square, or its unlocked oval/rectangle: 1″ on BOTH sides;
       absent    (a sticker on a sheet, any older caller): the 0.5″ rule as before.
     Never smaller than 0.5″ on any side, whichever rule applies. */
  var INDIVIDUAL_MIN_IN = 1;
  function individualMinimum(width, height, sizeRule) {
    if (sizeRule === 'diecut' && Math.max(width, height) < INDIVIDUAL_MIN_IN - 1e-9)
      return { code: 'size-below-individual-min', level: 'error', message: 'Individual die-cut stickers are at least 1″ on the longest side.' };
    if (sizeRule === 'shape' && Math.min(width, height) < INDIVIDUAL_MIN_IN - 1e-9)
      return { code: 'size-below-individual-min', level: 'error', message: 'Individual stickers are at least 1″ wide and 1″ tall.' };
    return null;
  }
  function builderRules(width, height, sizeRule) {
    function isQuarter(n) { return Math.abs(n / 0.25 - Math.round(n / 0.25)) < 1e-9; }
    return [function (/* meta */) {
      var out = [];
      if (!(width > 0) || !(height > 0)) return out;
      var ind = (width >= 0.5 && height >= 0.5) ? individualMinimum(width, height, sizeRule) : null;
      if (ind) out.push(ind);
      else if (width < 0.5 || height < 0.5) out.push({ code: 'size-too-small', level: 'error', message: 'Minimum finished size is 0.5″.' });
      else if (width > 10.5 || height > 10.5) out.push({ code: 'size-too-large', level: 'error', message: 'Maximum finished size is 10.5″.' });
      if (!isQuarter(width) || !isQuarter(height)) out.push({ code: 'size-increment', level: 'warn', message: 'Sizes use 0.25″ increments.' });
      return out;
    }];
  }

  function createCore(injected) {
    var eng = injected || defaultEngines();
    var U = eng.SLUpload, P = eng.SLPricing, S = eng.SLSheets;
    /* CART GRAPHICS (D-177) is a separate product with its own engine, exactly
       as sheets have SLSheets. Absent on every page that does not load it, and
       then nothing below can reach it: the sticker and sheet paths never read G. */
    var G = eng.SLCartGraphics || null;
    function isCartGraphics(t) { return !!G && t === G.PRODUCT_TYPE; }

    function initialState() {
      return {
        productType: 'diecut',            // 'diecut' | 'sheet'
        file: null,                       // { name, size } (the React layer holds the real File)
        uploadMeta: null, uploadWarnings: [], uploadStatus: null,
        width: 3, height: 3, quantity: 100,
        // Sticker Sheets (D-050): the customer picks the finished SHEET size (shared catalog) and the
        // NUMBER OF SHEETS. Both are ignored while productType === 'diecut'. Default sheet from SLSheets.
        // Default 10 sheets clears the pricing engine's existing 10-sticker minimum for ANY catalog
        // config (worst case 1 sticker/sheet × 10 = 10), so the initial sheet state is always priced.
        sheetSize: (S && S.DEFAULT_SHEET_SIZE_ID) || '8.5x10', sheetQuantity: 10,
        material: 'vinyl', finish: 'matte', border: 'none',
        // derived:
        pricing: null, sheet: null, eligibility: null, effectiveDpi: null,
        stickersPerSheet: null, totalStickers: null,   // sheet-mode derived (null for die-cut)
        warnings: [], readiness: { ready: false, canOrder: false, reasons: [] },
        pricingAvailable: true, loading: false, error: null
      };
    }

    // Route an uploaded file's bytes through SLUpload with the Builder's rules.
    function analyzeUpload(bytes, fileName, opts) {
      opts = opts || {};
      if (!U) return null;
      return U.analyzeBytes(bytes, fileName, {
        fileSize: opts.fileSize,
        targetWidthIn: opts.dpiWidth != null ? opts.dpiWidth : opts.width,     // drives effective print DPI
        targetHeightIn: opts.dpiHeight != null ? opts.dpiHeight : opts.height, // worst-axis DPI (SLUpload owns the math)
        /* The product decides its own size rules. A cart graphic is validated
           by its engine; everything else keeps the sticker rules unchanged. */
        rules: opts.rules || builderRules(opts.width != null ? opts.width : 3, opts.height != null ? opts.height : 3, opts.sizeRule)
      });
    }

    function diecutPrice(w, h, q) { return P ? P.diecut(w, h, q) : null; }        // delegate — no formula here
    function sheetPrice(w, h, q) { return (P && P.sheet) ? P.sheet(w, h, q) : null; } // delegate — sheet = die-cut × 0.92 (D-033)
    function sheetLayout(w, h, q, opts) { return S ? S.layout(w, h, q, opts) : null; } // delegate — no layout math here

    function sheetEligibility(sheet) {
      if (!sheet || sheet.status !== 'ok') return { eligibleForSheet: false, reason: sheet ? sheet.status : 'no-result' };
      // A single backed sticker (D-198) is one per sheet BY DESIGN; anything else needs more than one.
      var ok = sheet.stickersPerSheet > 1 || !!sheet.single;
      return { eligibleForSheet: ok, reason: ok ? 'ok' : 'one-per-sheet' };
    }

    function hasError(ws) { return ws.some(function (w) { return w.level === 'error'; }); }

    function readiness(st) {
      var reasons = [];
      var hasFile = !!st.file;
      var fileOk = st.uploadStatus ? st.uploadStatus === 'accepted' : true;
      var noErrors = !hasError(st.warnings);
      if (!hasFile) reasons.push('no-file');
      if (st.uploadStatus === 'rejected') reasons.push('file-rejected');
      if (!noErrors) reasons.push('validation-error');
      var priced = !!(st.pricing && !st.pricing.error);
      if (st.productType === 'sheet') {
        var eligible = !!(st.eligibility && st.eligibility.eligibleForSheet);
        if (!eligible) reasons.push('sheet-ineligible');
        if (!priced) reasons.push('sheet-price-unavailable');
        var sheetReady = hasFile && fileOk && noErrors && eligible && priced;  // sheets are priced (die-cut × 0.92, D-033)
        return { ready: sheetReady, canOrder: sheetReady, reasons: reasons };
      }
      if (!priced) reasons.push('not-priced');
      /* A cart graphic can be a valid, priced size that is not sold online yet
         (see SLCartGraphics.LAUNCH). That is a refusal to ORDER, not a
         validation error, and it carries its own reason. */
      if (isCartGraphics(st.productType) && st.availability && !st.availability.orderable) {
        reasons.push(st.availability.code);
        return { ready: false, canOrder: false, reasons: reasons };
      }
      var ready = hasFile && fileOk && noErrors && priced;
      return { ready: ready, canOrder: ready, reasons: reasons };
    }

    /* THE orchestrator. Pure derivation from the functional inputs — the React layer
       keeps the real File/bytes and calls this whenever an input changes.
       inputs: { productType, bytes, fileName, fileSize, width, height, quantity,
                 material, finish, border } */
    function recompute(inp) {
      inp = inp || {};
      var st = initialState();
      st.productType = inp.productType || 'diecut';
      st.width = +inp.width; st.height = +inp.height; st.quantity = +inp.quantity;
      if (inp.sheetSize != null) st.sheetSize = inp.sheetSize;
      if (inp.sheetQuantity != null) st.sheetQuantity = Math.max(1, Math.round(+inp.sheetQuantity) || 1);
      /* The sticker's exact outline, when the caller knows it better than the ordered
         size. Read by the sheet LAYOUT only; price and ordered size never see it. */
      var cutW = +inp.cutWidth > 0 ? +inp.cutWidth : null, cutH = +inp.cutHeight > 0 ? +inp.cutHeight : null;
      if (inp.material) st.material = inp.material;
      if (inp.finish) st.finish = inp.finish;
      if (inp.border) st.border = inp.border;
      /* ONE DEFINITION OF "HAS ARTWORK", AND THE BYTES ARE IT.
         ---------------------------------------------------------------------
         This read `inp.fileName ? {...} : null`, so `readiness()` — and through
         it the Add-to-Cart guard — decided whether the customer had artwork by
         asking whether a NAME STRING was truthy. Everything else in the funnel
         asks a different question: the preview, the price, the proof and the
         production export all run off `inp.bytes`.

         Two definitions of "has artwork" is one too many, and on a real phone
         they disagreed. An iPhone can hand back a File whose `.name` is empty
         — camera capture, a pasted image, some share-sheet and Files-provider
         paths — and the builder's main image path passed `file.name` through
         unmodified. The customer then had bytes, a rendered preview, a price
         and a Review Order screen, and the final guard alone said `no-file`.

         So presence is now decided by the artwork itself. A name is a LABEL for
         the artwork, not evidence of it, and when the platform declines to give
         one we supply it rather than refusing the order over it.

         THE SAFETY CHECK IS INTACT. No bytes and no name is still null, still
         `no-file`, and a genuinely empty builder is still refused — asserted in
         tests/cart-artwork-reconciliation.test.js. */
      var artworkSize = (inp.fileSize != null) ? inp.fileSize
                      : (inp.bytes && inp.bytes.length != null ? inp.bytes.length : null);
      var artworkName = inp.fileName || (inp.bytes ? 'artwork' : null);
      st.file = artworkName ? { name: artworkName, size: artworkSize } : null;
      /* THE CUSTOMER'S ANSWER TO THE RESOLUTION WARNING, carried so it can
         reach the ORDER. It was React state and nothing else: it died with the
         tab, never reached a line property, and HQ had no way to tell whether a
         soft-printing sticker had been accepted knowingly or slipped through.
         For a warning whose whole purpose is informed consent, that is the half
         that matters. Null when the artwork is fine or the question is
         unanswered — absence is not consent. */
      if (inp.qualityAck != null) st.qualityAck = inp.qualityAck;

      var warnings = [];
      var cart = isCartGraphics(st.productType);
      /* The cart graphic's own rules, and the artwork's own printed size for
         the resolution check when the builder supplies it (see
         SLCartGraphics.placedArtworkInches). */
      var productRules = cart ? G.sizeRules(st.width, st.height) : null;
      /* An individual sticker's minimum (see individualMinimum). A sheet keeps
         the 0.5″ rule; a die-cut payload that does not say which rule applies
         is held to the die-cut one. 'none' is the production host rebuilding
         an order already paid for at its own size. */
      var sizeRule = (st.productType === 'sheet' || inp.sizeRule === 'none') ? null : (inp.sizeRule === 'shape' ? 'shape' : 'diecut');
      if (cart) {
        st.cartGraphics = inp.cartGraphics || null;
        st.finish = G.resolveFinish(st.finish);
      }

      // 1) upload (SLUpload) — includes the Builder's size rules; recomputes DPI from targetWidthIn
      if (inp.bytes) {
        var up = analyzeUpload(inp.bytes, inp.fileName, { fileSize: inp.fileSize, width: st.width, height: st.height,
          rules: productRules, sizeRule: sizeRule, dpiWidth: cart ? inp.dpiWidthIn : null, dpiHeight: cart ? inp.dpiHeightIn : null });
        if (up) {
          st.uploadMeta = up.metadata; st.uploadWarnings = up.warnings; st.uploadStatus = up.status;
          st.effectiveDpi = up.metadata ? up.metadata.effectivePrintDpi : null;
          warnings = warnings.concat(up.warnings);
        }
      } else {
        // surface size validation even before a file is uploaded
        (productRules || builderRules(st.width, st.height, sizeRule))[0]().forEach(function (w) { warnings.push(w); });
      }

      // 2) product-specific derivation
      if (st.productType === 'sheet') {
        // Chosen-sheet mode (D-050): the customer picked the sheet size + number of sheets. The engine
        // yields stickersPerSheet and totalStickers; pricing stays BY STICKERS DELIVERED — one engine —
        // via SLPricing.sheet(w, h, totalStickers) (= die-cut × 0.92, D-033). No sheet-count pricing.
        st.sheet = sheetLayout(st.width, st.height, st.quantity, { sheet: st.sheetSize, sheetQuantity: st.sheetQuantity, cutWidth: cutW, cutHeight: cutH });
        st.eligibility = sheetEligibility(st.sheet);
        /* THE FEWEST SHEETS (SLSheets.minSheets; 10 for 4 x 6, Ryan 24 Oct 2026) is an order rule,
           so below it the line is refused here, not only in the builder's controls. Never for
           sizeRule 'none': the production host rebuilding an order that is already PAID. */
        if (S && S.minSheets && inp.sizeRule !== 'none' && st.sheet && st.sheet.status === 'ok' && !st.sheet.single) {
          var minN = S.minSheets(st.sheetSize, st.sheet.stickersPerSheet);
          if ((st.sheetQuantity || 0) < minN)
            warnings.push({ code: 'sheet-below-min', level: 'error', message: 'The minimum for this sheet is ' + minN + ' sheets.' });
        }
        st.stickersPerSheet = st.sheet ? st.sheet.stickersPerSheet : null;
        st.totalStickers = (st.sheet && st.sheet.totalStickers != null) ? st.sheet.totalStickers : null;
        var priceQty = (st.totalStickers != null) ? st.totalStickers : st.quantity;
        // Payload quantity for sheets IS the total sticker count that pricing is based on, so per-unit
        // price and the quantity-as-price transport stay coherent (cart shows sheets/perSheet/total).
        if (st.totalStickers != null) st.quantity = st.totalStickers;
        /* A SINGLE STICKER (D-198) is priced as the regular die-cut sticker it is: the sticker's own
           size and count, no sheet discount, never the backing. Sheets stay die-cut × 0.92 (D-033). */
        st.pricing = (st.sheet && st.sheet.single) ? diecutPrice(st.width, st.height, priceQty)
                                                   : sheetPrice(st.width, st.height, priceQty);
        st.pricingAvailable = !!(st.pricing && !st.pricing.error);
        if (st.sheet && st.sheet.status === 'ok' && !st.eligibility.eligibleForSheet)
          warnings.push({ code: 'sheet-ineligible', level: 'info', message: 'This size makes one sticker per sheet — try a larger sheet or order as an Individual Sticker.' });
        if (st.sheet && (st.sheet.status === 'unsupported-on-sheet' || st.sheet.status === 'unsupported-sheet-product'))
          warnings.push({ code: 'sheet-too-big', level: 'error', message: 'This sticker size does not fit the selected sheet. Choose a larger sheet or a smaller sticker.' });
        if (st.pricing && st.pricing.error) warnings.push({ code: 'price-' + st.pricing.error, level: 'warn', message: 'Sheet price unavailable for this quantity.' });
      } else if (cart) {
        /* CART GRAPHICS: area-priced by its own engine, quantity × unit price,
           no tiers. SLPricing is not consulted, so no sticker price, tier or
           quantity limit applies here and none of them can be moved by it. */
        st.pricing = G.price(st.width, st.height, st.quantity);
        st.pricingAvailable = !!(st.pricing && !st.pricing.error);
        st.availability = G.availability(st.width, st.height, st.quantity,
          { shape: st.cartGraphics ? st.cartGraphics.shape : null,
            borderIn: st.cartGraphics ? st.cartGraphics.borderWidthIn : null });
        if (st.pricing && st.pricing.error === 'invalid-quantity')
          warnings.push({ code: 'quantity-invalid', level: 'error', message: G.validateQuantity(st.quantity).message });
        if (!st.availability.orderable && st.availability.code !== 'invalid-size')
          warnings.push({ code: st.availability.code, level: 'info', message: st.availability.message });
      } else {
        st.pricingAvailable = true;
        st.pricing = diecutPrice(st.width, st.height, st.quantity);
        if (st.pricing && st.pricing.error) warnings.push({ code: 'price-' + st.pricing.error, level: 'warn', message: 'Price unavailable for this selection.' });
      }

      st.warnings = warnings;
      st.readiness = readiness(st);
      return st;
    }

    /* ---- stateful store: deterministic actions + subscribe (React adaptor drives this) ----
       The React Builder is a presentation/adaptor layer: it calls these actions and renders
       getState(); it must not re-implement any engine logic. */
    var inputs = { productType: 'diecut', bytes: null, fileName: null, fileSize: null,
      width: 3, height: 3, quantity: 100,
      sheetSize: (S && S.DEFAULT_SHEET_SIZE_ID) || '8.5x10', sheetQuantity: 10,
      material: 'vinyl', finish: 'matte', border: 'none' };
    var state = recompute(inputs);
    var subs = [];
    function notify() { for (var i = 0; i < subs.length; i++) { try { subs[i](state); } catch (e) {} } }
    function commit() { state = recompute(inputs); notify(); return state; }

    function setProductType(t) { inputs.productType = (t === 'sheet') ? 'sheet' : 'diecut'; return commit(); }
    function setDimensions(w, h) { if (w != null) inputs.width = +w; if (h != null) inputs.height = +h; return commit(); }
    function setQuantity(q) { inputs.quantity = +q; return commit(); }
    // Sticker Sheets (D-050): sheet size = a shared-catalog id (or {widthIn,heightIn}); sheet quantity
    // = whole number of sheets (min 1). Both are no-ops for die-cut but kept in state across a switch.
    function setSheetSize(s) { inputs.sheetSize = s; return commit(); }
    function setSheetQuantity(n) { inputs.sheetQuantity = Math.max(1, Math.round(+n) || 1); return commit(); }
    function setMaterial(m) { inputs.material = m; return commit(); }
    function setFinish(f) { inputs.finish = f; return commit(); }
    function setBorder(b) { inputs.border = b; return commit(); }
    function analyzeArtwork(bytes, fileName, fileSize) {
      inputs.bytes = bytes || null; inputs.fileName = fileName || null;
      inputs.fileSize = (fileSize != null) ? fileSize : (bytes ? bytes.length : null);
      return commit();
    }
    function clearArtwork() { inputs.bytes = null; inputs.fileName = null; inputs.fileSize = null; return commit(); }
    // Browser convenience: read a File then analyze. Returns a Promise of the new state.
    function analyzeFile(file) {
      if (!file) return Promise.reject(new Error('no file'));
      var read = file.arrayBuffer ? file.arrayBuffer()
        : new Promise(function (res, rej) { var r = new FileReader(); r.onload = function () { res(r.result); }; r.onerror = function () { rej(r.error); }; r.readAsArrayBuffer(file); });
      return read.then(function (buf) { return analyzeArtwork(new Uint8Array(buf), file.name, file.size); });
    }
    function recalculate() { return commit(); }
    function getState() { return state; }
    function getInputs() { return Object.assign({}, inputs); }
    function subscribe(fn) { if (typeof fn === 'function') subs.push(fn); return function () { var i = subs.indexOf(fn); if (i >= 0) subs.splice(i, 1); }; }

    /* ---- canonical visual-transform model (D-041) — ONE model for ALL interfaces (desktop + mobile) ----
       Screen-independent by design: `offset` is NORMALIZED to the frame (a fraction of the face w/h) so
       the design reconstructs at any pixel size — it is NOT bound to desktop pixel coordinates. This does
       NOT affect pricing or the cart transport. Desktop and mobile are presentation layers over this. */
    var TRANSFORM_VERSION = 1;
    function defaultTransform() {
      return {
        version: TRANSFORM_VERSION,
        shape: 'custom',                       // 'custom'(die-cut) | 'circle' | 'square' | 'rounded' | 'rectangle'
        scale: 1,                              // zoom; 1 = fit baseline
        offset: { x: 0, y: 0 },                // NORMALIZED translation (fraction of frame w/h); {0,0} = centered
        rotation: 0,                           // degrees
        flip: { x: false },                    // horizontal flip
        border: { color: '#2E86E3', thickness: 'standard' },
        artworkRectIn: null,             // derived production geometry; see setTransform
        faceColor: 'white',                    // shaped-sticker background fill
        /* WHICH ARTWORK THE CUSTOMER APPROVED. Background removal produces a
           second picture from the same upload, and the production export
           carries whichever one the customer chose. Pixels alone cannot say
           whether that choice was made deliberately or whether something
           upstream damaged the file, so the choice itself travels. False for
           every order that never touched the control, which is most of them. */
        backgroundRemoved: false,
        backgroundRemovalMethod: null          // which engine produced it, when one did
      };
    }
    function cloneTransform(t) {
      return { version: t.version, shape: t.shape, scale: t.scale,
        offset: { x: t.offset.x, y: t.offset.y }, rotation: t.rotation,
        flip: { x: !!t.flip.x }, border: { color: t.border.color, thickness: t.border.thickness },
        artworkRectIn: t.artworkRectIn || null,
        faceColor: t.faceColor,
        backgroundRemoved: !!t.backgroundRemoved,
        backgroundRemovalMethod: t.backgroundRemovalMethod || null };
    }
    var transform = defaultTransform();
    var proofRequested = false;                // proof is requested in the cart; the payload can carry it
    function getTransform() { return cloneTransform(transform); }
    function setTransform(patch) {
      patch = patch || {};
      if (patch.shape != null) transform.shape = patch.shape;
      if (patch.scale != null && isFinite(patch.scale)) transform.scale = +patch.scale;
      if (patch.offset) {
        if (patch.offset.x != null && isFinite(patch.offset.x)) transform.offset.x = +patch.offset.x;
        if (patch.offset.y != null && isFinite(patch.offset.y)) transform.offset.y = +patch.offset.y;
      }
      if (patch.rotation != null && isFinite(patch.rotation)) transform.rotation = +patch.rotation;
      if (patch.flip && patch.flip.x != null) transform.flip.x = !!patch.flip.x;
      if (patch.border) {
        if (patch.border.color != null) transform.border.color = patch.border.color;
        if (patch.border.thickness != null) transform.border.thickness = patch.border.thickness;
      }
      if (patch.faceColor != null) transform.faceColor = patch.faceColor;
      /* DERIVED PRODUCTION GEOMETRY, not a customer choice. Kept beside the
         transform because it is part of reproducing the design, and set to
         null explicitly when it does not apply so a stale rect from a
         previous shape can never travel with a new one. */
      if (patch.artworkRectIn !== undefined) {
        transform.artworkRectIn = patch.artworkRectIn || null;
      }
      /* THE APPROVED ARTWORK. Set together, and always explicitly: a removal
         that is switched back off must clear the method too, or an order would
         carry the name of an engine whose output it is not printing. */
      if (patch.backgroundRemoved !== undefined) {
        transform.backgroundRemoved = !!patch.backgroundRemoved;
        if (!transform.backgroundRemoved) transform.backgroundRemovalMethod = null;
      }
      if (patch.backgroundRemovalMethod !== undefined && transform.backgroundRemoved) {
        transform.backgroundRemovalMethod = patch.backgroundRemovalMethod || null;
      }
      return getTransform();
    }
    function resetTransform() { transform = defaultTransform(); return getTransform(); }
    function setProofRequested(v) { proofRequested = !!v; return proofRequested; }
    // Deterministic normalized→pixel geometry for a frame {w,h} (px). Same canonical state → proportional
    // geometry at ANY frame size, so desktop and mobile render the identical intended design. Pure.
    function geometryOf(t, frame) {
      t = t || defaultTransform(); frame = frame || {};
      var w = +frame.w || 0, h = +frame.h || 0;
      return {
        offsetPx: { x: (t.offset ? +t.offset.x : 0) * w, y: (t.offset ? +t.offset.y : 0) * h },
        scale: (t.scale != null) ? +t.scale : 1,
        rotation: t.rotation || 0,
        flipX: !!(t.flip && t.flip.x)
      };
    }
    function transformGeometry(frame) { return geometryOf(transform, frame); }

    /* ---- THE PROOF DRAW PLAN (pure) ---------------------------------------
       What the customer's proof image is made of, as data. The React builder
       executes this against a canvas; nothing about canvas, DOM or images
       appears here, so the geometry that decides what the customer approved
       is unit-testable and lives in exactly one place.

       ── WHY IT IS HERE AND NOT IN THE BUILDER ───────────────────────────
       It was in the builder, twice: once as the CSS transform on the visible
       art, once as a canvas routine that bailed out entirely for die-cut and
       handed back the upload URL instead. So `_artwork_preview` on live order
       #1014 was byte-identical to `_artwork_original` — same sha1, same
       1920x1080, same 486,372 bytes. The order carried two copies of the
       customer's file and no record of the design they actually approved.

       TWO GEOMETRIES EXIST, AND ONLY TWO:
         die-cut  the art IS the sticker. No ring, no face fill, no clip —
                  the cut line is baked into the art — and the art is laid
                  out at the box WIDTH with a free height.
         shaped   a border ring around a filled face, art letterboxed
                  (object-fit: contain) into the box.

       ORDER OF OPERATIONS is the CSS order and must stay it: translate to the
       box centre plus the offset, then scale, then rotate, then flip. Any
       other order moves the design.

       @param {Object} g   { boxW, boxH, dieCut, padPx, shapeRad, faceRad,
                             stickerBg, faceBg, fit, scale, x, y, rotDeg, flipX }
       @param {number} natW natural pixel width of the source image
       @param {number} natH natural pixel height
       @returns {Object} the plan */
    function proofDrawPlan(g, natW, natH) {
      g = g || {};
      var Sw = Math.max(1, Math.round(+g.boxW || 1));
      var Sh = Math.max(1, Math.round(+g.boxH || 1));
      var die = !!g.dieCut;
      var pad = die ? 0 : Math.max(0, +g.padPx || 0);
      var nw = +natW > 0 ? +natW : 1;
      var nh = +natH > 0 ? +natH : 1;
      var imgAR = nw / nh;

      /* Art size before the transform. */
      var dW, dH;
      if (g.fit === 'width') {
        dW = Sw; dH = Sw / imgAR;
      } else {
        if (imgAR > Sw / Sh) { dW = Sw; dH = Sw / imgAR; }
        else { dH = Sh; dW = Sh * imgAR; }
      }

      return {
        canvas: { w: Sw, h: Sh },
        /* Null for die-cut: no ring is painted and nothing is clipped to it. */
        ring: die ? null : {
          x: 0, y: 0, w: Sw, h: Sh,
          radius: g.shapeRad,
          fill: (g.stickerBg && g.stickerBg !== 'transparent') ? g.stickerBg : null,
          clip: true
        },
        face: die ? null : {
          x: pad, y: pad, w: Sw - 2 * pad, h: Sh - 2 * pad,
          radius: g.faceRad,
          fill: (g.faceBg && g.faceBg !== 'transparent') ? g.faceBg : null,
          clip: true
        },
        art: {
          /* Drawn centred on the origin AFTER the transform below. */
          w: dW, h: dH,
          translate: { x: Sw / 2 + (+g.x || 0), y: Sh / 2 + (+g.y || 0) },
          scale: (g.scale != null && isFinite(g.scale)) ? +g.scale : 1,
          rotateRad: ((+g.rotDeg || 0) * Math.PI) / 180,
          flipX: !!g.flipX
        }
      };
    }

    /* ---- THE CORNER RADIUS RULE (pure) ------------------------------------
       `plan.ring.radius` / `plan.face.radius` carry the CSS the screen uses —
       '16px', '50%', '999px' or '0' — because that is what the builder already
       had and rewriting it would have meant re-deriving the shape. Every
       executor of the plan (canvas proof, production SVG) has to turn that
       string into a number, and each one doing it privately is how two
       renderers come to disagree about the same sticker. So the rule lives
       here, once.

       '50%' and '999px' are both the builder's way of saying "fully round";
       anything else is a pixel value that can never exceed half the short side
       (a larger radius would invert the corner). */
    function resolveRadius(val, box) {
      var b = +box > 0 ? +box : 0;
      if (val === '50%' || val === '999px') return b / 2;
      var n = parseFloat(val);
      return isFinite(n) ? Math.min(Math.max(n, 0), b / 2) : 0;
    }

    /* ---- THE BORDER RULE (pure) -------------------------------------------
       How thick the border ring is, in PREVIEW PIXELS, for a box of a given
       size showing a sticker of a given physical size.

       ── THE DEFECT THIS REPLACES ─────────────────────────────────────────
       The builder computed this twice, and the two did not agree:

         desktop   thicknessIn * (maxPx / longestIn)   — physically correct
         mobile    vfrac * maxPx                       — a viewport fraction

       The mobile rule has no term for the sticker's physical size, so the ring
       occupied a fixed slice of the phone's screen no matter what was ordered.
       On a 1" sticker the Standard 0.05" border resolved to roughly 9.7px
       where physics wanted 22px; on a 3" sticker it was too thick instead.
       Nothing on screen looked broken, which is why it survived — but the
       proof, the canonical config and any production file generated from that
       geometry all inherit the error, and a wrong-width ring PRINTS.

       There is now one rule, and it is the physical one. Desktop and mobile
       differ only in how big the preview box is; the border occupies the same
       fraction of the sticker in both, which is what makes them the same
       design.

       ── AND NO CLAMPS ────────────────────────────────────────────────────
       The desktop expression also carried a 2px floor and a cap at 10% of the
       short side. Both are gone, because both corrupt the very thing this
       function exists to guarantee.

       A clamp is a function of the BOX, not of the sticker, so it binds at
       different physical thicknesses on different box sizes — which reinstates
       the platform disagreement through the back door. A 0.03" ring on a 6"
       sticker clears the 2px floor on desktop (2.38px) and hits it on a phone
       (1.83px), so the two would resolve 0.030" and 0.033" for one order. The
       cap is worse than cosmetic: a 0.10" border on a 0.5" sticker is 20% of
       the short side, so the cap would silently halve a border the customer
       chose and PAID for, and the production file would print the halved one.

       Neither clamp was protecting anything real. A 1.8px antialiased ring is
       perfectly visible, and a thick border on a small sticker is a legitimate
       order that renders fine (0.10" on 0.5" leaves 60% of the face). If some
       combination is genuinely undesirable, that is a product rule about what
       the builder OFFERS — not something geometry may quietly rewrite after
       the customer has chosen it.

       So this returns the exact physical value, unrounded. Fractional pixels
       are fine for CSS and canvas alike, and rounding here would put a
       half-pixel of error into a file measured in inches. */
    /* ---- THE BORDER LEVEL RULE (pure) --------------------------------------
       A border level is a PROPORTION of the sticker, not a fixed width.

       ── WHY THIS CHANGED ─────────────────────────────────────────────────
       `thin/standard/thick` used to be fixed inches: 0.03 / 0.05 / 0.10, the
       same width on a half-inch sticker as on a nine-inch one. Physically
       consistent, and visually wrong — the same Standard border is 10% of a
       0.5" sticker and 0.56% of a 9" one, so a customer resizing their design
       watched the border they had chosen apparently melt away. Thin stopped
       looking thin; Thick stopped looking thick.

       The fractions are not invented. They are the `vfrac` values the original
       design lock already specified for how thick each level should LOOK, and
       they land almost exactly on today's fixed widths at 2.1"-2.9" — the most
       common sticker sizes — so the change is invisible where most orders live
       and only corrects the extremes.

       ── AND WHY IT IS CLAMPED ────────────────────────────────────────────
       An unbounded percentage is unsafe at both ends. At 0.5" a Thin border
       would be 0.007" — thinner than kiss-cut registration tolerance, so it
       can disappear or go negative on one side of the cut. At 9" a Thick
       border would be 0.315", which stops being a border and becomes a mat.

       MIN is 0.01" rather than something rounder because 0.02" COLLAPSES THE
       LEVELS: at the smallest sizes Thin and Standard both clamp to the same
       number and the customer's choice stops meaning anything. Checked across
       the whole 0.5"-9" range at every 0.25" step, 0.01"/0.25" keeps
       thin < standard < thick everywhere, with no two levels ever equal.

       This is the ONE place the number is decided. The screen, the proof and
       the production file all read it from here, so they cannot disagree. */
    var BORDER_LEVEL_FRAC = { thin: 0.014, standard: 0.022, thick: 0.035 };
    var BORDER_MIN_IN = 0.01, BORDER_MAX_IN = 0.25;

    /* THE CORNER RADIUS IS PHYSICAL TOO (D-092).
       ---------------------------------------------------------------------
       Exactly the defect `borderPxFor` was written to kill, in the one piece
       of geometry that never got the same treatment. The radius came from
       `shapeRadius()`, which returned a FIXED '30px' — a number of PREVIEW
       PIXELS, with no term for the sticker's physical size and none for the
       box it is drawn in. So it was a fraction of the SCREEN.

       Measured on the real box maths, same order, rounded corners:

         6x6in   desktop 0.3782in   iPhone SE 0.5128in   = 3.42 mm apart
         4x4in   desktop 0.2830in   iPhone SE 0.3419in   = 1.50 mm apart

       That is a cut path that differs by device. It also drifted across sizes
       on desktop alone — 8.67% of the edge at 1in, 6.30% at 6in — because a
       constant pixel count is a shrinking fraction of a growing box.

       0.08 is chosen to sit on top of what desktop already draws at the small
       and popular sizes (8.67% at 1in, 8.06% at 2in, 7.54% at 3in), so the
       common case barely moves; what changes is that every case now agrees.
       Clamped at both ends: a radius may never exceed half the short side,
       which `resolveRadius` already enforces downstream. */
    var CORNER_LEVEL_FRAC = { rounded: 0.08, sharp: 0.005 };
    var CORNER_MIN_IN = 0.01, CORNER_MAX_IN = 0.5;

    /**
     * The canonical physical corner radius.
     *
     * @param {String} corners    'rounded' | 'sharp'
     * @param {Number} longestIn  the sticker's longest physical side
     * @returns {Number} inches
     */
    function cornerInchesForLevel(corners, longestIn) {
      var f = CORNER_LEVEL_FRAC[corners === 'sharp' ? 'sharp' : 'rounded'];
      var L = +longestIn;
      if (!(f > 0) || !(L > 0)) return 0;
      return Math.min(CORNER_MAX_IN, Math.max(CORNER_MIN_IN, L * f));
    }

    /**
     * The same radius in PREVIEW PIXELS for a given box — the corner twin of
     * `borderPxFor`, and derived the same way so the two can never disagree.
     *
     * @returns {Number} pixels
     */
    function cornerPxFor(corners, longestIn, boxW, boxH) {
      var L = +longestIn, bw = +boxW, bh = +boxH;
      if (!(L > 0) || !isFinite(bw) || !isFinite(bh)) return 0;
      var maxPx = Math.max(bw, bh);
      if (!(maxPx > 0)) return 0;
      return cornerInchesForLevel(corners, L) * (maxPx / L);
    }

    /* A PRODUCT MAY FIX ITS BORDER WIDTHS.
       The proportional rule above is the sticker rule, and it stays the
       default. A product far larger than a sticker (Cart Graphics) reaches the
       cap at every level, so it supplies one fixed width per level instead.
       The table is held on this core instance and set only by that product's
       page, so it still is the one place the number is decided: screen, proof
       and production file read it through the same function. Null restores
       the proportional rule. A table missing a level is refused whole rather
       than half-applied. */
    var borderWidthTable = null;
    function setBorderWidthTable(table) {
      if (table == null) { borderWidthTable = null; return true; }
      var ok = ['thin', 'standard', 'thick'].every(function (k) { return isFinite(+table[k]) && +table[k] > 0; });
      if (!ok) return false;
      borderWidthTable = { thin: +table.thin, standard: +table.standard, thick: +table.thick };
      return true;
    }

    /**
     * @param {String} level   'thin' | 'standard' | 'thick'
     * @param {Number} longestIn  the sticker's longest physical side
     * @returns {Number} the canonical physical border, in inches
     */
    function borderInchesForLevel(level, longestIn) {
      var f = BORDER_LEVEL_FRAC[level];
      var L = +longestIn;
      if (!(f > 0) || !(L > 0)) return 0;
      if (borderWidthTable) return borderWidthTable[level];
      return Math.min(BORDER_MAX_IN, Math.max(BORDER_MIN_IN, L * f));
    }
    function borderLevels() {
      if (borderWidthTable) return { frac: BORDER_LEVEL_FRAC, minIn: BORDER_MIN_IN, maxIn: BORDER_MAX_IN, fixedIn: borderWidthTable };
      return { frac: BORDER_LEVEL_FRAC, minIn: BORDER_MIN_IN, maxIn: BORDER_MAX_IN };
    }

    function borderPxFor(thicknessIn, longestIn, boxW, boxH) {
      var t = +thicknessIn, L = +longestIn, bw = +boxW, bh = +boxH;
      /* Every input is checked rather than coerced. A `|| 0` here would let a
         NaN box dimension fall through to the other edge and return a
         confident number for geometry nobody could compute — the shape of
         error this whole module exists to stop. */
      if (!(t > 0) || !(L > 0) || !isFinite(bw) || !isFinite(bh)) return 0;
      var maxPx = Math.max(bw, bh);
      if (!(maxPx > 0)) return 0;
      return t * (maxPx / L);
    }
    /** The inverse, for tests and for anything reporting what was actually drawn. */
    function borderInchesFor(borderPx, longestIn, boxW, boxH) {
      var maxPx = Math.max(+boxW || 0, +boxH || 0);
      var L = +longestIn;
      if (!(maxPx > 0) || !(L > 0)) return 0;
      return (+borderPx || 0) / (maxPx / L);
    }

    /* ---- pure touch-gesture math (D-043) — shared by desktop + mobile; no DOM, so it is unit-testable
       and never duplicated in a mobile fork. The UI calls these to compute canonical values from touch. */
    var ZOOM_MIN = 0.4, ZOOM_MAX = 4;
    function clampZoom(z) { z = +z; if (!isFinite(z)) return 1; return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)); }
    function pinchZoom(baseScale, startDist, dist) {   // two-finger pinch → clamped scale
      if (!(startDist > 0) || !(dist > 0)) return clampZoom(baseScale);
      return clampZoom(+(baseScale * (dist / startDist)).toFixed(3));
    }
    function normalizeAngle(deg) { deg = +deg; if (!isFinite(deg)) return 0; return ((deg % 360) + 360) % 360; }
    function spinRotate(baseDeg, deltaDeg) { return normalizeAngle((+baseDeg || 0) + (+deltaDeg || 0)); }

    /* ---- pure sticker-size math (independent Width/Height + Lock Proportions) — shared desktop/mobile,
       DOM-free and unit-testable so the mobile Size panel never forks the sizing rules. Sizes are inches on
       the existing 0.25" grid, clamped to the 0.50"–9.00" product limits. Ratio is always width / height. */
    var SIZE_MIN_IN = 0.5, SIZE_MAX_IN = 9, SIZE_STEP_IN = 0.25;
    function sizeLimits() { return { min: SIZE_MIN_IN, max: SIZE_MAX_IN, step: SIZE_STEP_IN, individualMin: INDIVIDUAL_MIN_IN }; }
    function snapSizeIn(v) {                       // nearest 0.25", clamped to [0.50, 9.00]
      v = +v;
      if (!isFinite(v)) return SIZE_MIN_IN;
      v = Math.round(v / SIZE_STEP_IN) * SIZE_STEP_IN;
      return +Math.min(SIZE_MAX_IN, Math.max(SIZE_MIN_IN, v)).toFixed(2);
    }
    // Locked partner: derive the OTHER dimension from the one the customer moved, using a STABLE ratio.
    // Always deriving the partner from the driver (never chaining partner→driver→partner) is what keeps
    // repeated stepping free of cumulative rounding drift. ratio<=0 / non-finite falls back to 1:1 (square).
    function lockHeightIn(widthIn, ratio) { widthIn = snapSizeIn(widthIn); return snapSizeIn(ratio > 0 ? widthIn / ratio : widthIn); }
    function lockWidthIn(heightIn, ratio) { heightIn = snapSizeIn(heightIn); return snapSizeIn(ratio > 0 ? heightIn * ratio : heightIn); }

    /* ---- durable design id (D-041 / D-056) -----------------------------------------
       The stable handle tying an order line to its three production assets (original
       file, rendered preview, canonical config). Production scans it; reorders reuse it.

       GENERATED AND STORED — deliberately NOT derived from the payload.

       A content hash looks attractive (same design -> same id, no state needed) and was
       tried first. It loses on the property that actually matters: the hash covers the
       payload, so ADDING ANY PAYLOAD FIELD SILENTLY RE-IDENTIFIES EVERY DESIGN EVER
       ISSUED. The payload has already gone v1 -> v2 -> v3; under a hash, a design
       ordered at v3 and reordered at v4 would no longer match itself, which destroys the
       one guarantee the id exists to provide. It also made a reorder at a different
       quantity produce a different id, and left the id hostage to the hash function, the
       key-sort rule and float formatting — four frozen contracts, none enforced.

       The governing principle: a value that is RECOMPUTED can change; a value that is
       STORED cannot. A durable identifier is assigned once and persisted.

       So the id is minted ONCE at the add-to-cart boundary and travels with the line
       item. `assemblePayload` never invents one — it returns `designId: null` unless the
       caller supplies it, which is what keeps the payload deterministic and testable.

       TIME-ORDERED. The first 9 characters encode the creation time, fixed width, in an
       alphabet whose character order matches its numeric order — so plain lexicographic
       sorting of ids is chronological. That is what makes the future orphan-cleanup pass
       (D-056, post-launch debt) tractable: recent uploads group together by name. */

    // Crockford base32: no I, L, O or U — so an id can be read aloud or typed in the shop
    // without 1/I or 0/O confusion, and cannot accidentally spell anything. Its characters
    // are in ascending ASCII order, which is what makes ids sort chronologically as text.
    var B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
    var DESIGN_ID_TIME_CHARS = 9;    // 45 bits — enough for epoch-ms past the year 3000
    var DESIGN_ID_RAND_CHARS = 6;    // 30 bits of randomness per millisecond

    /* Fixed-width base32. Uses % and / rather than bitwise ops on purpose: `n & 31`
       coerces to int32, and epoch milliseconds (~1.8e12) overflow that badly. */
    function b32(n, chars) {
      var s = '';
      n = Math.floor(n);
      for (var i = 0; i < chars; i++) { s = B32.charAt(n % 32) + s; n = Math.floor(n / 32); }
      return s;
    }

    /** 30 bits from the platform CSPRNG where available, Math.random otherwise. */
    function randomBits30() {
      var g = (typeof globalThis !== 'undefined') ? globalThis : {};
      var c = g.crypto;
      if (c && typeof c.getRandomValues === 'function') {
        var a = new Uint32Array(1);
        c.getRandomValues(a);
        return a[0] % 1073741824;
      }
      return Math.floor(Math.random() * 1073741824);
    }

    /**
     * Mint a new durable design id: "SL-XXXXX-XXXXX-XXXXX".
     *
     * Grouped in fives for reading off a job ticket. The separators sit at fixed offsets,
     * so the grouping does not disturb chronological string sorting.
     *
     * @param {number} [nowMs] injectable clock — tests pin it; production omits it.
     */
    function newDesignId(nowMs) {
      var t = (typeof nowMs === 'number' && isFinite(nowMs)) ? nowMs : Date.now();
      var raw = b32(t, DESIGN_ID_TIME_CHARS) + b32(randomBits30(), DESIGN_ID_RAND_CHARS);
      return 'SL-' + raw.slice(0, 5) + '-' + raw.slice(5, 10) + '-' + raw.slice(10, 15);
    }

    /** The creation time encoded in an id, or null if it isn't one of ours. */
    function designIdCreatedAtMs(id) {
      if (typeof id !== 'string') return null;
      var raw = id.replace(/^SL-/, '').replace(/-/g, '');
      if (raw.length !== DESIGN_ID_TIME_CHARS + DESIGN_ID_RAND_CHARS) return null;
      var t = 0;
      for (var i = 0; i < DESIGN_ID_TIME_CHARS; i++) {
        var v = B32.indexOf(raw.charAt(i));
        if (v < 0) return null;
        t = t * 32 + v;
      }
      return t;
    }

    /* ---- canonical Builder payload (Shopify-INDEPENDENT; deterministic, no timestamps) ----
       Everything production needs, assembled from the derived state. Shopify consumes this via
       sl-cart-adapter.js; the Builder is never coupled to Shopify. `extra.artworkRef` is the
       caller-supplied artwork handle (object URL today; a Shopify file id later). */
    function assemblePayload(st, extra) {
      extra = extra || {};
      var m = st.uploadMeta;
      var payload = {
        builderVersion: 'builder-core-v1',
        productType: st.productType,
        artwork: {
          fileName: st.file ? st.file.name : (extra.fileName || null),
          fileSize: st.file ? st.file.size : (extra.fileSize != null ? extra.fileSize : null),
          reference: extra.artworkRef != null ? extra.artworkRef : null,
          format: m ? m.format : null,
          kind: m ? m.kind : null,
          widthPx: m ? m.widthPx : null,
          heightPx: m ? m.heightPx : null,
          hasAlpha: m ? m.hasAlpha : null,
          orientation: m ? m.orientation : null
        },
        dimensions: { widthIn: st.width, heightIn: st.height },
        quantity: st.quantity,
        options: { material: st.material, finish: st.finish, border: st.border },
        effectiveDpi: st.effectiveDpi,
        /* The TOKEN of the artwork that was acknowledged, not a bare `true`.
           An ack has to name the file it covers, or a later upload inherits a
           consent nobody gave for it — the same reason the builder keys
           `qualityAckFor` to the artwork token rather than to a boolean. */
        qualityAck: st.qualityAck || null,
        pricing: st.pricing || null,          // die-cut result, OR sheet = die-cut × 0.92 (D-033)
        pricingAvailable: st.pricingAvailable, // true when a live price exists (die-cut or sheet)
        sheet: st.sheet || null,               // SLSheets result when productType === 'sheet'
        /* Cart Graphics only, null otherwise: what the product is made of, the
           finished shape and border in physical terms, and the cart-preview
           measurements — which are REFERENCE ONLY and change nothing above. */
        cartGraphics: st.cartGraphics || null,
        availability: st.availability || null,
        eligibility: st.eligibility || null,
        warnings: st.warnings,
        readiness: st.readiness,
        // Canonical visual transform (D-041) — shared desktop/mobile model, normalized for
        // screen-independent reconstruction. Does not affect pricing or the cart transport.
        transform: (function () {
          var t = cloneTransform(transform);
          /* ── TWO CROP MODELS, AND THE ORDER SAYS WHICH ───────────────────
             `normalized-v1`   the artwork sits inside a FIXED frame — a
                               circle, a square, a rounded rectangle. The shape
                               is the mask, so pan, zoom and rotation are real
                               and cropping outside the shape is what the
                               customer asked for. Reconstructing the design
                               means reproducing that crop.

             `contour-fit-v1`    a die-cut sticker, where the ARTWORK IS THE
                               SILHOUETTE. There is no frame to position
                               inside, so there is no crop: offset is 0, zoom
                               is 1, rotation is 0, and only the flip and the
                               physical size are the customer's to choose.

             ── WHY A MODEL NAME AND NOT `offset === 0` ────────────────────
             A legacy die-cut design that was never dragged also records a zero
             offset, and the two mean different things: one is "the customer
             left it centred", the other is "positioning does not exist here".
             A reader that cannot tell them apart will eventually reproduce a
             crop that was never made, or fail to reproduce one that was. The
             model is stated so nobody has to infer it.

             Historic orders keep `normalized-v1` and their existing handling.
             Nothing here reinterprets them. */
          var isContour = (t.shape === 'custom');
          if (isContour) {
            /* AT THE RECORD, NOT ONLY AT THE CONTROLS. The UI no longer offers
               pan, zoom or rotation for a contour design — but React state can
               go stale across a shape change, and a cropped order is not a
               thing this system should be ABLE to emit. So the canonical
               record states the model's invariants rather than copying
               whatever the last interaction happened to leave behind. */
            t.scale = 1;
            t.offset = { x: 0, y: 0 };
            t.rotation = 0;
          }
          // Deterministic crop/reconstruction model — source dims + normalized scale/offset + rotation +
          // output aspect. Intentionally NOT desktop pixel coordinates (reconstructs at any screen size).
          t.crop = {
            model: isContour ? 'contour-fit-v1' : 'normalized-v1',
            sourceWidthPx: m ? m.widthPx : null,
            sourceHeightPx: m ? m.heightPx : null,
            outputAspect: (st.width && st.height) ? +(st.width / st.height).toFixed(6) : null,
            scale: t.scale,
            offset: { x: t.offset.x, y: t.offset.y },   // normalized (fraction of frame)
            rotation: t.rotation,
            flipX: t.flip.x,
            /* CONTOUR-FIT ONLY. Where the artwork sits inside the ordered
               sticker, in inches from the production canvas top-left. The
               finished CutContour spans the ordered size, so the artwork no
               longer does, and a reader that assumed it would be wrong by
               the border width on every side. */
            artworkRectIn: isContour ? (t.artworkRectIn || null) : null
          };
          return t;
        })(),
        proofRequested: (extra.proofRequested != null) ? !!extra.proofRequested : proofRequested,
        pricingEngineVersion: (isCartGraphics(st.productType)) ? G.version : ((P && P.version) ? P.version : 'diecut-v1'),
        // v3 adds `designId`. Bumped rather than left at v2 so a consumer can tell from the
        // payload alone whether a durable id is present, instead of probing for the field.
        schemaVersion: 'payload-v3'
      };
      /* NEVER minted here. The payload is deterministic by contract — same inputs, same
         bytes — and generating an id inside it would break that. The id is minted once at
         the add-to-cart boundary (`newDesignId`) and passed back in via `extra.designId`,
         which is also how a reorder or a restored saved design keeps pointing at artwork
         that is already stored instead of creating a second record for the same files. */
      payload.designId = (extra.designId != null && extra.designId !== '')
        ? String(extra.designId)
        : null;
      return payload;
    }
    function buildPayload(inputs, extra) { return assemblePayload(recompute(inputs), extra); }
    function getPayload(extra) { return assemblePayload(state, extra); }

    return {
      // pure (used by tests + internally):
      initialState: initialState, builderRules: builderRules, individualMinimum: individualMinimum, analyzeUpload: analyzeUpload,
      diecutPrice: diecutPrice, sheetLayout: sheetLayout, sheetEligibility: sheetEligibility,
      recompute: recompute, buildPayload: buildPayload, engines: eng,
      newDesignId: newDesignId, designIdCreatedAtMs: designIdCreatedAtMs,
      defaultTransform: defaultTransform, geometryOf: geometryOf,
      proofDrawPlan: proofDrawPlan,
      resolveRadius: resolveRadius,
      borderPxFor: borderPxFor, borderInchesFor: borderInchesFor,
      borderInchesForLevel: borderInchesForLevel, borderLevels: borderLevels,
      setBorderWidthTable: setBorderWidthTable,
      cornerInchesForLevel: cornerInchesForLevel, cornerPxFor: cornerPxFor,
      clampZoom: clampZoom, pinchZoom: pinchZoom, spinRotate: spinRotate,
      sizeLimits: sizeLimits, snapSizeIn: snapSizeIn, lockHeightIn: lockHeightIn, lockWidthIn: lockWidthIn,
      // stateful actions:
      setProductType: setProductType, setDimensions: setDimensions, setQuantity: setQuantity,
      setSheetSize: setSheetSize, setSheetQuantity: setSheetQuantity,
      setMaterial: setMaterial, setFinish: setFinish, setBorder: setBorder,
      analyzeArtwork: analyzeArtwork, analyzeFile: analyzeFile, clearArtwork: clearArtwork,
      recalculate: recalculate, getState: getState, getInputs: getInputs, getPayload: getPayload,
      getTransform: getTransform, setTransform: setTransform, resetTransform: resetTransform,
      transformGeometry: transformGeometry, setProofRequested: setProofRequested,
      subscribe: subscribe
    };
  }

  var API = { version: 'builder-core-v1', create: createCore, builderRules: builderRules };
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) { root.SLBuilderCore = API; try { root.slBuilder = createCore(); } catch (e) {} }
})(typeof window !== 'undefined' ? window : null);
